import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  KeyIcon,
  MoonIcon,
  ServerStackIcon,
  ShareIcon,
  SunIcon,
} from '@heroicons/react/16/solid';
import { Avatar } from '../ui/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownHeader,
  DropdownHeading,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
  DropdownTick,
} from '../ui/dropdown';
import { SidebarItem } from '../ui/sidebar';
import { isStaff, useSession } from '../lib/session';
import { useTheme } from '../lib/theme';

/**
 * Who you are, how the console looks, and the way out — shared by the
 * console shell and the customiser so the two screens can't drift.
 *
 * It lives at the bottom of the sidebar on both: the customiser is a
 * full-screen tool that replaces the shell, so without this the account
 * row and the appearance switch would simply vanish while you were in it.
 */

/** The platform's appearance switch — a per-person preference for the
 *  whole console rather than a property of any one screen. */
function ThemeItems() {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: 'system' as const, label: 'System', icon: <ComputerDesktopIcon /> },
    { value: 'light' as const, label: 'Light', icon: <SunIcon /> },
    { value: 'dark' as const, label: 'Dark', icon: <MoonIcon /> },
  ];
  return (
    <>
      <DropdownHeader>
        <div className="text-xs/5 text-fg-2">Appearance</div>
      </DropdownHeader>
      {options.map((o) => (
        <DropdownItem key={o.value} onClick={() => setTheme(o.value)}>
          {o.icon}
          <DropdownLabel>{o.label}</DropdownLabel>
          {theme === o.value && <DropdownTick />}
        </DropdownItem>
      ))}
    </>
  );
}

export function AccountMenu({
  anchor,
  who,
  onSignOut,
}: {
  anchor: 'top start' | 'bottom end';
  who: string;
  onSignOut: () => void;
}) {
  const { me } = useSession();
  const staff = isStaff(me);
  return (
    <DropdownMenu className="min-w-64" anchor={anchor}>
      <DropdownHeader>
        <div className="truncate text-xs/5 text-fg-2">Signed in as</div>
        <div className="truncate text-sm/6 font-medium text-fg">{who}</div>
      </DropdownHeader>
      <DropdownDivider />
      {/* Staff only, and fleet-wide rather than per-organisation: here
          rather than as a sidebar section on every page. Not shown to
          the people the API would refuse. */}
      {staff ? (
        <>
          <DropdownSection>
            <DropdownHeading>Platform</DropdownHeading>
            <DropdownItem href="/tokens">
              <KeyIcon />
              <DropdownLabel>Tokens</DropdownLabel>
            </DropdownItem>
            {/* The network-wide view: the only place members with no
                owning organisation are visible at all. */}
            <DropdownItem href="/content-network">
              <ShareIcon />
              <DropdownLabel>Content network</DropdownLabel>
            </DropdownItem>
            {/* Every pool box and its site containers. */}
            <DropdownItem href="/fleet/health">
              <ServerStackIcon />
              <DropdownLabel>Fleet health</DropdownLabel>
            </DropdownItem>
          </DropdownSection>
          <DropdownDivider />
        </>
      ) : null}
      <ThemeItems />
      <DropdownDivider />
      <DropdownItem onClick={onSignOut}>
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Sign out</DropdownLabel>
      </DropdownItem>
    </DropdownMenu>
  );
}

/** The identity line the sidebar footer shows, with ` · staff` for a
 *  super admin — the same string the old console header carried. */
export function useAccountIdentity() {
  const { me, signOut } = useSession();
  const who = `${me?.user?.email ?? me?.label ?? ''}${me?.user?.global_role === 'super_admin' ? ' · staff' : ''}`;
  const initials = (me?.user?.name ?? me?.user?.email ?? me?.label ?? '?').slice(0, 2).toUpperCase();
  return { me, who, initials, signOut: () => { void signOut(); } };
}

export const BRAND_AVATAR = 'bg-avatar text-fg';

/** The account row itself, for a SidebarFooter. */
export function AccountFooterItem() {
  const { me, who, initials, signOut } = useAccountIdentity();
  return (
    <Dropdown>
      <DropdownButton as={SidebarItem}>
        <span className="flex min-w-0 items-center gap-3">
          <Avatar square initials={initials} alt="" className={`size-10 ${BRAND_AVATAR}`} />
          <span className="min-w-0">
            <span className="block truncate text-sm/5 font-medium text-fg">
              {me?.user?.name ?? 'Signed in'}
            </span>
            <span className="block truncate text-xs/5 font-normal text-fg-3">{who}</span>
          </span>
        </span>
        <ChevronUpIcon />
      </DropdownButton>
      <AccountMenu anchor="top start" who={who} onSignOut={signOut} />
    </Dropdown>
  );
}
